// src/admin/pages/admin/AdminProPolk.jsx
import React, { useState, useEffect } from "react";
import { proPolkService, imageService } from "../../../services/api";
import { toast } from "react-toastify";

const AdminProPolkPage = () => {
  const [fields, setFields] = useState({
    title: "", quoteTop: "", quoteBottom: "",
    photo: "", logo: "", text: "",
  });
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [uploading, setUploading] = useState({ photo: false, logo: false });

  useEffect(() => { fetchContent(); }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const { data: d } = await proPolkService.get();
      setFields({
        title:       d.title       ?? "",
        quoteTop:    d.quoteTop    ?? "",
        quoteBottom: d.quoteBottom ?? "",
        photo:       d.photo       ?? "",
        logo:        d.logo        ?? "",
        text:        d.text        ?? "",
      });
    } catch {
      toast.error("Не вдалося завантажити контент");
    } finally {
      setLoading(false);
    }
  };

  const set = (key) => (value) => setFields((prev) => ({ ...prev, [key]: value }));
  const handleChange = (key) => (e) => set(key)(e.target.value);

  const handleFileUpload = async (key, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Оберіть файл зображення"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Максимум 5MB"); return; }
    try {
      setUploading((p) => ({ ...p, [key]: true }));
      const link = await imageService.upload(file, `/pro_polk/${key}/${Date.now()}_${file.name}`);
      set(key)(link);
      toast.success("Завантажено!");
    } catch {
      toast.error("Помилка при завантаженні");
    } finally {
      setUploading((p) => ({ ...p, [key]: false }));
      e.target.value = "";
    }
  };

  const handleSave = async () => {
    window.scrollTo(0, 0);
    try {
      setSaving(true);
      await proPolkService.update(fields);
      toast.success("Збережено!");
    } catch {
      toast.error("Помилка при збереженні");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={styles.loading}>Завантаження...</div>;

  return (
    <div style={styles.pageContainer}>
      <h1 style={styles.pageTitle}>Про полк</h1>

      <div style={styles.form}>

        {/* ── Text row ── */}
        <div style={styles.row}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Заголовок:</label>
            <input style={styles.input} type="text" placeholder="210-й окремий штурмовий полк"
              value={fields.title} onChange={handleChange("title")} />
          </div>
        </div>

        <div style={styles.twoCol}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Цитата — верх:</label>
            <input style={styles.input} type="text" placeholder="Текст верхньої цитати..."
              value={fields.quoteTop} onChange={handleChange("quoteTop")} />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Цитата — низ:</label>
            <input style={styles.input} type="text" placeholder="Текст нижньої цитати..."
              value={fields.quoteBottom} onChange={handleChange("quoteBottom")} />
          </div>
        </div>

        {/* ── Image row ── */}
        <div style={styles.twoCol}>
          {(["photo", "logo"] as const).map((key) => (
            <div key={key} style={styles.imageRow}>
              <div style={styles.imagePreviewBox}>
                {fields[key]
                  ? <img src={fields[key]} alt={key} style={styles.imageThumb} />
                  : <span style={styles.imagePlaceholder}>—</span>
                }
              </div>
              <div style={styles.imageInputs}>
                <label style={styles.label}>{key === "photo" ? "Фото:" : "Логотип:"}</label>
                <input style={styles.input} type="text" placeholder="URL..."
                  value={fields[key]} onChange={handleChange(key)} />
                <div style={styles.imageActions}>
                  <label style={styles.uploadBtn} htmlFor={`upload-${key}`}>
                    {uploading[key] ? "..." : "📁"}
                  </label>
                  <input type="file" id={`upload-${key}`} accept="image/*"
                    style={{ display: "none" }} disabled={uploading[key]}
                    onChange={(e) => handleFileUpload(key, e)} />
                  {fields[key] && (
                    <button style={styles.clearBtn} onClick={() => set(key)("")} title="Очистити">✕</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Text ── */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Основний текст:</label>
          <textarea style={styles.textarea} placeholder="Введіть текст сторінки..."
            value={fields.text} onChange={handleChange("text")} />
        </div>

        <div style={styles.buttonRow}>
          <button onClick={handleSave} disabled={saving} style={{
            ...styles.saveButton,
            backgroundColor: saving ? "#ccc" : "green",
            cursor: saving ? "not-allowed" : "pointer",
          }}>
            {saving ? "Збереження..." : "Зберегти"}
          </button>
        </div>

      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  pageContainer: {
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    padding: "2rem",
  },
  pageTitle: {
    marginBottom: "1.5rem",
    fontSize: "1.8rem",
    color: "var(--secondary-color)",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    padding: "1.5rem",
    backgroundColor: "#e9e9e9",
    borderRadius: "8px",
  },
  row:       { display: "flex", flexDirection: "column" },
  twoCol:    { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" },
  formGroup: { display: "flex", flexDirection: "column" },
  label:     { marginBottom: "0.35rem", fontWeight: 600, fontSize: "0.9rem" },
  input: {
    padding: "0.6rem 0.8rem",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    outline: "none",
    fontSize: "14px",
  },

  // Image row
  imageRow: {
    display: "flex",
    gap: "0.75rem",
    alignItems: "flex-start",
  },
  imagePreviewBox: {
    width: "72px",
    height: "72px",
    flexShrink: 0,
    border: "1px solid #ddd",
    borderRadius: "6px",
    backgroundColor: "#eee",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  imageThumb: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  imagePlaceholder: { color: "#bbb", fontSize: "1.2rem" },
  imageInputs: { flex: 1, display: "flex", flexDirection: "column", gap: "0.35rem" },
  imageActions: { display: "flex", gap: "0.5rem", alignItems: "center" },
  uploadBtn: {
    padding: "0.3rem 0.7rem",
    backgroundColor: "#007bff",
    color: "white",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "0.85rem",
    userSelect: "none",
  },
  clearBtn: {
    padding: "0.3rem 0.6rem",
    backgroundColor: "#dc3545",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "0.8rem",
  },

  textarea: {
    width: "100%",
    minHeight: "360px",
    padding: "0.8rem",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontFamily: "monospace",
    fontSize: "13px",
    lineHeight: "1.5",
    resize: "vertical",
    outline: "none",
    boxSizing: "border-box",
  },
  buttonRow: { display: "flex", justifyContent: "flex-end" },
  saveButton: {
    padding: "0.75rem 1.5rem",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontFamily: "'UAFSans', sans-serif",
    fontWeight: 500,
    fontSize: "1rem",
  },
  loading: { padding: "2rem", textAlign: "center", color: "#666" },
};

export default AdminProPolkPage;